import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { safe } from '@k2/common/helpers';
import { AddMessage } from '@k2/common/flash-messages/actions';
import { RootState } from '@k2/common/flash-messages/state';
import { AppStore } from '@k2/common/state/services/app-store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

const unknownError = {
  text: 'An unexpected error has occurred! Our technical team have been notified.'
};
type Msg = { text: string };

@Injectable()
export class FlashMessagesHttpInterceptor implements HttpInterceptor {
  constructor(private readonly store: AppStore<RootState>) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(tap(this.processOkResponse, this.processErrorResponse));
  }

  private processOkResponse = (response: HttpResponse<any>) => {
    const messages = safe(() => response.body.meta.flash) || [];

    messages
      .map(({ text, type }) => new AddMessage({ text, type: type || 'confirm' }))
      .forEach(msg => this.store.dispatch(msg));
  };

  private processErrorResponse = (response: HttpErrorResponse) => {
    const raw =
      safe(() => response.error?.meta?.flash) ??
      safe(() => response.error) ??
      null;

    const messages: Array<{ text: string }> =
      this.coerceToMessages(raw).length ? this.coerceToMessages(raw) : [unknownError];

    messages
      .map(({ text }) => new AddMessage({ text, type: 'error' }))
      .forEach(msg => this.store.dispatch(msg));
  };



  private coerceToMessages = (input: unknown): Msg[] => {
    if (input == null) return [];

    if (Array.isArray(input)) {
      if (input.every(x => typeof x === 'string')) {
        return (input as string[]).map(text => ({ text }));
      }
      return (input as any[]).map((x) => {
        if (x && typeof x === 'object') {
          const o = x as Record<string, any>;
          const text =
            typeof o.text === 'string' ? o.text
              : typeof o.message === 'string' ? o.message
                : typeof o.error === 'string' ? o.error
                  : JSON.stringify(o);
          return { text };
        }
        return { text: String(x) };
      });
    }

    if (typeof input === 'string') return [{ text: input }];

    if (typeof input === 'object') {
      const o = input as Record<string, any>;
      const text =
        typeof o.text === 'string' ? o.text
          : typeof o.message === 'string' ? o.message
            : typeof o.error === 'string' ? o.error
              : JSON.stringify(o);
      return [{ text }];
    }

    return [{ text: String(input) }];
  }
}

export const httpInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: FlashMessagesHttpInterceptor,
  multi: true
};
